import { create } from "zustand";
import {
  studentsControllerGetUsersSubscriptionStatus,
  type GetSubscriptionStatusDTO,
} from "@oneapp/data";
import type {
  SubscriptionStatusRequest,
  SubscriptionStatusResponse,
} from "../../../shared/types";

type SubStatus = "idle" | "loading" | "loaded" | "error";

interface SubscriptionEntry {
  status: SubStatus;
  subscribed: boolean;
}

export interface ProgramData {
  program_id: string;
  gradeId: number;
  examId: number;
  examName: string;
  mdsExamId?: number;
}

const initialEntry: SubscriptionEntry = { status: "idle", subscribed: false };

const buildPayload = (
  testSeriesType: string,
  program: ProgramData,
): SubscriptionStatusRequest => ({
  module: "test",
  grade: program.gradeId,
  cmsExams: [{ id: String(program.examId), name: program.examName }],
  testType: { name: testSeriesType },
  testseriesType: testSeriesType,
  mdsExamId: program.mdsExamId,
  programId: program.program_id,
});

interface SubscriptionState {
  statuses: Record<string, SubscriptionEntry>;
  programId: string | null;
  programData: ProgramData | null;
  _fetchPromises: Record<string, Promise<boolean> | null>;
  fetchForTypes: (types: string[], program: ProgramData) => void;
  fetchOne: (testSeriesType: string, program?: ProgramData) => Promise<boolean>;
  isSubscribed: (testSeriesType: string) => boolean;
  getEntryStatus: (testSeriesType: string) => SubStatus;
  waitForType: (testSeriesType: string) => Promise<boolean>;
  reset: () => void;
}

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  statuses: {},
  programId: null,
  programData: null,
  _fetchPromises: {},

  fetchForTypes: (types, program) => {
    const state = get();

    if (state.programId !== program.program_id) {
      set({
        statuses: {},
        programId: program.program_id,
        programData: program,
        _fetchPromises: {},
      });
    } else if (!state.programData) {
      set({ programData: program });
    }

    for (const testSeriesType of types) {
      const current = get().statuses[testSeriesType] ?? initialEntry;
      if (current.status === "loading" || current.status === "loaded") continue;
      get().fetchOne(testSeriesType, program);
    }
  },

  fetchOne: (testSeriesType, program) => {
    const resolvedProgram = program ?? get().programData;
    if (!resolvedProgram) return Promise.resolve(false);

    const existing = get()._fetchPromises[testSeriesType];
    if (existing) return existing;

    set((state) => ({
      statuses: {
        ...state.statuses,
        [testSeriesType]: { status: "loading", subscribed: false },
      },
      programData: state.programData ?? resolvedProgram,
    }));

    const promise = (async () => {
      try {
        const payload = buildPayload(testSeriesType, resolvedProgram);
        const response = (await studentsControllerGetUsersSubscriptionStatus(
          payload as unknown as GetSubscriptionStatusDTO,
          { clientName: "oneApp" },
        )) as unknown as SubscriptionStatusResponse;

        const subscribed = response.data?.subscribeStatus ?? false;
        set((state) => ({
          statuses: {
            ...state.statuses,
            [testSeriesType]: { status: "loaded", subscribed },
          },
        }));
        return subscribed;
      } catch {
        set((state) => ({
          statuses: {
            ...state.statuses,
            [testSeriesType]: { status: "error", subscribed: false },
          },
        }));
        return false;
      } finally {
        set((state) => ({
          _fetchPromises: {
            ...state._fetchPromises,
            [testSeriesType]: null,
          },
        }));
      }
    })();

    set((state) => ({
      _fetchPromises: { ...state._fetchPromises, [testSeriesType]: promise },
    }));

    return promise;
  },

  isSubscribed: (testSeriesType) =>
    get().statuses[testSeriesType]?.subscribed ?? false,

  getEntryStatus: (testSeriesType) =>
    get().statuses[testSeriesType]?.status ?? "idle",

  waitForType: async (testSeriesType) => {
    const state = get();
    const entry = state.statuses[testSeriesType] ?? initialEntry;

    if (entry.status === "loaded" || entry.status === "error") {
      return entry.subscribed;
    }

    const existing = state._fetchPromises[testSeriesType];
    if (existing) return existing;

    return entry.subscribed;
  },

  reset: () =>
    set({
      statuses: {},
      programId: null,
      programData: null,
      _fetchPromises: {},
    }),
}));
